/**
 * What the Month view shows, shaped from the lanes' own data and the plan
 * check. Pure.
 *
 * The Month view was rebuilt round the plan check on 2026-10-31 (Akash's
 * decisions, the approved `MonthView` mockup within them). A cell no longer
 * lists tasks: it says how many there were or are planned, draws them as
 * one thin bar by task type (solid for what happened, outlined for what is
 * still to come) and badges what needs a look -- work that didn't finish,
 * a fire alarm or drill, and from today on the cleaning zones the plan
 * leaves past their target -- and, last, blocked time on the day. Days are
 * the SITE's (`DateKey`), like the lanes', and so is the week: Monday to
 * Sunday.
 *
 * Nothing here re-decides what is on the calendar: the items are the
 * lanes' (`useCalendarLaneData`: live tasks, finished ones, the plan;
 * automatic charging left out), filed by the day they start on. The plan
 * check is the api-server's answer, worded here and never recomputed.
 */
import type { PlanCheck, PlanCheckGap, PlanCheckUnscheduledZone } from '../../../api/v1';
import type { LanePaint } from '../calendar-categories';
import { DIDNT_FINISH, paintOf, type LaneItem, type LaneOutcome } from './lane-items';
import {
  SERVICE_LABEL,
  SERVICE_ORDER,
  serviceOfLane,
  type RobotEntry,
  type RobotService,
} from './lane-layout';
import { LANE_PAINTS } from './lane-style';
import {
  MONTH_SHORT,
  WEEKDAY_LONG,
  WEEKDAY_SHORT,
  addDays,
  dateKeyOf,
  dayWindow,
  instantAt,
  overlaps,
  weekStart,
  weekdayOf,
  type DateKey,
  type TimeWindow,
} from './lane-time';
import type { SiteBar } from './site-row';
import { formatZonedClock, zonedParts } from '../zoned-time';

export const MONTH_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

function parts(key: DateKey): { year: number; month: number; day: number } {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return { year: y, month: m, day: d };
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

// ---- the month -------------------------------------------------------------------

/** The first of `key`'s month. */
export function monthStartKey(key: DateKey): DateKey {
  const { year, month } = parts(key);
  return `${year}-${pad(month)}-01`;
}

/** The last day of `key`'s month. */
export function monthEndKey(key: DateKey): DateKey {
  return addDays(addMonths(monthStartKey(key), 1), -1);
}

/** `key` moved by whole calendar months, its day clamped to the target
 *  month's length: 31 January + 1 is 28 (or 29) February, never March. */
export function addMonths(key: DateKey, n: number): DateKey {
  const { year, month, day } = parts(key);
  const first = new Date(Date.UTC(year, month - 1 + n, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return `${first.getUTCFullYear()}-${pad(first.getUTCMonth() + 1)}-${pad(Math.min(day, last))}`;
}

/** Same month, same year. */
export function sameMonth(a: DateKey, b: DateKey): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

/** Every day of `key`'s month, in order. */
export function monthDayKeys(key: DateKey): DateKey[] {
  const end = monthEndKey(key);
  const out: DateKey[] = [];
  for (let d = monthStartKey(key); d <= end; d = addDays(d, 1)) out.push(d);
  return out;
}

/** The grid: whole Monday-to-Sunday weeks covering the month, five or six
 *  of them. The days either side belong to the next and previous months
 *  and are drawn empty. */
export function monthWeeks(key: DateKey): DateKey[][] {
  const end = monthEndKey(key);
  const weeks: DateKey[][] = [];
  for (let w = weekStart(monthStartKey(key)); w <= end; w = addDays(w, 7)) {
    weeks.push(Array.from({ length: 7 }, (_, i) => addDays(w, i)));
  }
  return weeks;
}

/** The month on the site's clock, midnight on the 1st to midnight on the
 *  1st of the next: what the screen reads occurrences and windows for. */
export function monthWindow(key: DateKey, timeZone: string): TimeWindow {
  return {
    fromMs: instantAt(monthStartKey(key), 0, timeZone),
    toMs: instantAt(addMonths(monthStartKey(key), 1), 0, timeZone),
  };
}

/** The days the plan check is asked about: the whole month, or nothing
 *  when it is all in the past (decision 5: a past day shows what happened
 *  and is not checked). */
export function monthPlanRange(
  key: DateKey,
  todayKey: DateKey,
): { fromDate: DateKey; toDate: DateKey } | null {
  const toDate = monthEndKey(key);
  if (toDate < todayKey) return null;
  return { fromDate: monthStartKey(key), toDate };
}

/** "November 2026". */
export function monthTitle(key: DateKey): string {
  const { year, month } = parts(key);
  return `${MONTH_LONG[month - 1]} ${year}`;
}

// ---- words for a day -----------------------------------------------------------

export type DayKind = 'past' | 'today' | 'future';

export function dayKind(key: DateKey, todayKey: DateKey): DayKind {
  return key < todayKey ? 'past' : key === todayKey ? 'today' : 'future';
}

/** "Tue 03/Nov", the calendar's own short day. */
export function dayShort(key: DateKey): string {
  const { month, day } = parts(key);
  return `${WEEKDAY_SHORT[weekdayOf(key)]} ${pad(day)}/${MONTH_SHORT[month - 1]}`;
}

/** "Tuesday 03/Nov", the day panel's title. */
export function dayTitle(key: DateKey): string {
  const { month, day } = parts(key);
  return `${WEEKDAY_LONG[weekdayOf(key)]} ${pad(day)}/${MONTH_SHORT[month - 1]}`;
}

/** "Tuesday 3 November", for a screen reader. */
export function daySpoken(key: DateKey): string {
  const { month, day } = parts(key);
  return `${WEEKDAY_LONG[weekdayOf(key)]} ${day} ${MONTH_LONG[month - 1]}`;
}

/** "12 Nov"; "12 Nov 2025" when not `thisYear`'s. */
export function dayMonth(key: DateKey, thisYear?: number): string {
  const { year, month, day } = parts(key);
  const y = thisYear !== undefined && year !== thisYear ? ` ${year}` : '';
  return `${day} ${MONTH_SHORT[month - 1]}${y}`;
}

/** A run of days: "12 Nov", "12-15 Nov", "30 Nov-2 Dec"; the years too
 *  when they differ ("30 Dec 2026-2 Jan 2027"). */
export function spanText(from: DateKey, to: DateKey): string {
  if (from === to) return dayMonth(from);
  const a = parts(from);
  const b = parts(to);
  if (a.year !== b.year) return `${dayMonth(from, b.year)}-${dayMonth(to, a.year)}`;
  if (a.month === b.month) return `${a.day}-${dayMonth(to)}`;
  return `${dayMonth(from)}-${dayMonth(to)}`;
}

/** An instant as the site's day: "10 Nov", or "10 Nov 2025" when it is
 *  not this year. */
export function msDayMonth(ms: number, timeZone: string, todayKey: DateKey): string {
  return dayMonth(dateKeyOf(ms, timeZone), parts(todayKey).year);
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

// ---- what each day holds ---------------------------------------------------------

/** Still to come: an occurrence not yet due, or a task in a robot's queue.
 *  Everything else happened, or is happening. */
export function isStillToCome(outcome: LaneOutcome): boolean {
  return outcome === 'scheduled' || outcome === 'queued';
}

export interface MonthSegment {
  paint: LanePaint;
  /** Happened (or happening): solid. */
  happened: number;
  /** Still to come: outlined. */
  toCome: number;
}

export interface MonthDay {
  key: DateKey;
  kind: DayKind;
  /** The day's work, the hidden types left out, and so are the runs a
   *  blocked time holds (`held`). */
  items: LaneItem[];
  /** Runs still to come that a blocked time (a maintenance window) holds:
   *  the runner will not send them, so they are not planned (decision 3,
   *  the plan check's own rule). Counted out of every figure and named on
   *  the blocked time's line instead (review, 2026-10-31: "3 planned" and
   *  "Busiest 08–10" over a day whose plan check said those runs were
   *  held). */
  held: LaneItem[];
  total: number;
  done: number;
  didntFinish: number;
  toCome: number;
  /** One per type present, in the legend's order. */
  segments: MonthSegment[];
  /** "Fire alarm" or "Fire drill" when one held the site that day. */
  fire: string | null;
  /** A blocked time (maintenance window) touches the day: `[start, end)`
   *  overlaps local midnight to the next, so one ending at midnight does
   *  not touch the day after (decision 11, Akash 2026-10-31). The cell's
   *  "Blocked" badge, past days and days to come alike. */
  blocked: boolean;
  /** Maintenance windows and fire events touching the day. */
  siteBars: SiteBar[];
}

/** A run the runner will not send: one still to come whose start falls in
 *  a blocked time, start included and end not, as the dispatch gate reads
 *  a window (`maintenance.covering_window`). A task already in a robot's
 *  queue has been sent; nothing holds it. */
export function isHeld(it: LaneItem, bars: readonly SiteBar[]): boolean {
  return (
    it.kind === 'occurrence' &&
    it.outcome === 'scheduled' &&
    bars.some((b) => b.kind === 'maintenance' && b.startMs <= it.startMs && it.startMs < b.endMs)
  );
}

/**
 * Each day of the month, from the lanes' items and site bars. An item is
 * filed under the day it starts on (an overnight clean belongs to the
 * evening it began), and a type the legend hides is left out of every
 * figure, as is a run a blocked time holds (`isHeld`).
 */
export function buildMonthDays(args: {
  monthKey: DateKey;
  todayKey: DateKey;
  timeZone: string;
  items: readonly LaneItem[];
  siteBars: readonly SiteBar[];
  hidden: ReadonlySet<LanePaint>;
}): Map<DateKey, MonthDay> {
  const days = new Map<DateKey, MonthDay>();
  for (const key of monthDayKeys(args.monthKey)) {
    const window = dayWindow(key, args.timeZone);
    const bars = args.siteBars.filter((b) =>
      overlaps({ fromMs: b.startMs, toMs: Math.max(b.endMs, b.startMs + 1) }, window),
    );
    const fires = bars.filter((b) => b.kind === 'fire');
    days.set(key, {
      key,
      kind: dayKind(key, args.todayKey),
      items: [],
      held: [],
      total: 0,
      done: 0,
      didntFinish: 0,
      toCome: 0,
      segments: [],
      // A real alarm outranks a drill on a day that had both.
      fire:
        fires.length === 0 ? null : fires.some((f) => !f.fire?.drill) ? 'Fire alarm' : 'Fire drill',
      blocked: bars.some((b) => b.kind === 'maintenance'),
      siteBars: bars,
    });
  }
  for (const it of args.items) {
    if (args.hidden.has(paintOf(it))) continue;
    const day = days.get(dateKeyOf(it.startMs, args.timeZone));
    if (day) (isHeld(it, args.siteBars) ? day.held : day.items).push(it);
  }
  for (const day of days.values()) {
    day.items.sort((a, b) => a.startMs - b.startMs || a.id.localeCompare(b.id));
    day.held.sort((a, b) => a.startMs - b.startMs || a.id.localeCompare(b.id));
    const byPaint = new Map<LanePaint, MonthSegment>();
    for (const it of day.items) {
      const paint = paintOf(it);
      const seg = byPaint.get(paint) ?? { paint, happened: 0, toCome: 0 };
      if (isStillToCome(it.outcome)) {
        seg.toCome += 1;
        day.toCome += 1;
      } else {
        seg.happened += 1;
      }
      byPaint.set(paint, seg);
      if (it.outcome === 'done') day.done += 1;
      if (DIDNT_FINISH.has(it.outcome)) day.didntFinish += 1;
    }
    day.total = day.items.length;
    day.segments = LANE_PAINTS.filter((p) => byPaint.has(p)).map((p) => byPaint.get(p)!);
  }
  return days;
}

/** The types on the month's calendar, in the legend's order: what the
 *  legend offers, with any type already hidden kept so it can be shown
 *  again. */
export function monthPaints(
  items: readonly LaneItem[],
  hidden: ReadonlySet<LanePaint>,
): LanePaint[] {
  const present = new Set<LanePaint>(items.map(paintOf));
  return LANE_PAINTS.filter((p) => present.has(p) || hidden.has(p));
}

/** What a cell says in its corner: "12 tasks" for a past day, "12
 *  planned" from today on; nothing for an empty day. */
export function cellCount(day: MonthDay): string {
  if (day.total === 0) return '';
  return day.kind === 'past' ? plural(day.total, 'task', 'tasks') : `${day.total} planned`;
}

export function zonesShortText(n: number): string {
  return `${plural(n, 'zone', 'zones')} short`;
}

export function didntFinishText(n: number): string {
  return `${n} didn’t finish`;
}

/** The whole cell for a screen reader: "Tuesday 3 November: 12 planned,
 *  2 zones short"; "..., blocked time, 2 runs held" on a day blocked time
 *  touches. */
export function cellLabel(day: MonthDay, shortZones: number | null): string {
  const bits: string[] = [];
  if (day.total === 0) bits.push(day.kind === 'past' ? 'no tasks' : 'nothing planned');
  else bits.push(cellCount(day));
  if (day.blocked) bits.push('blocked time');
  if (day.held.length > 0) bits.push(`${plural(day.held.length, 'run', 'runs')} held`);
  if (day.didntFinish > 0) bits.push(didntFinishText(day.didntFinish));
  if (day.fire) bits.push(day.fire.toLowerCase());
  if (shortZones) bits.push(zonesShortText(shortZones));
  const today = day.kind === 'today' ? ', today' : '';
  return `${daySpoken(day.key)}${today}: ${bits.join(', ')}`;
}

// ---- the day panel --------------------------------------------------------------

export interface BusiestHours {
  fromHour: number;
  toHour: number;
  count: number;
}

/** The two hours of the day with the most work starting in them (the
 *  earliest, on a tie); null when no two hours hold more than one piece of
 *  work, where the earliest hour won a tie of ones and said nothing (review,
 *  2026-10-31). On the site's clock, so a daylight-saving day still reads
 *  08-10. */
export function busiestHours(
  items: readonly LaneItem[],
  key: DateKey,
  timeZone: string,
): BusiestHours | null {
  if (items.length === 0) return null;
  let best: BusiestHours | null = null;
  for (let h = 0; h <= 22; h += 1) {
    const from = instantAt(key, h, timeZone);
    const to = instantAt(key, h + 2, timeZone);
    const count = items.filter((it) => it.startMs >= from && it.startMs < to).length;
    if (count > 1 && (!best || count > best.count)) best = { fromHour: h, toHour: h + 2, count };
  }
  return best;
}

/** "08–10" for a tile; "08:00–10:00" spelled out. */
export function busiestText(b: BusiestHours, long = false): string {
  const end = b.toHour === 24 ? (long ? '24:00' : '24') : pad(b.toHour) + (long ? ':00' : '');
  return `${pad(b.fromHour)}${long ? ':00' : ''}–${end}`;
}

export interface ServiceCount {
  service: RobotService;
  label: string;
  total: number;
  didntFinish: number;
}

/**
 * The day's work by the services this site has robots for -- counts only
 * (decision 6: no forecasts, no robots off). A linen robot's delivery is
 * Linen's. A service with robots and no work that day says 0, which is
 * itself worth seeing; work no service here covers (charging, docking) is
 * not counted.
 */
export function byService(
  items: readonly LaneItem[],
  robots: readonly Pick<RobotEntry, 'service'>[],
): ServiceCount[] {
  const services = new Set(robots.map((r) => r.service));
  const out = SERVICE_ORDER.filter((s) => s !== 'other' && services.has(s)).map(
    (service): ServiceCount => ({
      service,
      label: SERVICE_LABEL[service],
      total: 0,
      didntFinish: 0,
    }),
  );
  for (const it of items) {
    const service = it.paint === 'linen' ? 'linen' : serviceOfLane(it.lane);
    const row = out.find((r) => r.service === service);
    if (!row) continue;
    row.total += 1;
    if (DIDNT_FINISH.has(it.outcome)) row.didntFinish += 1;
  }
  return out;
}

/**
 * A maintenance window or fire event that touched the day, as the day
 * panel lists it, on the site's clock: "Blocked 06:00–08:00 · Lift
 * servicing", "Fire drill 14:05–14:20". One that runs over midnight
 * names both its days, on either of them: "Blocked Tue 17/Nov 14:00 – Wed
 * 18/Nov 18:00". On the 18th it read "14:00–18:00", as if it began that
 * afternoon (integration check, 2026-10-31), and then "Tue 17/Nov
 * 14:00–18:00", as if it ended on the 17th (final gate, 2026-10-31). A
 * blocked time says how many of the day's runs it holds ("· 2 runs
 * held"), which the day's figures leave out (`MonthDay.held`).
 */
export function siteBarLine(
  b: SiteBar,
  key: DateKey,
  timeZone: string,
  held: readonly LaneItem[] = [],
): string {
  const overnight =
    dateKeyOf(b.startMs, timeZone) !== key || (!b.ongoing && dateKeyOf(b.endMs, timeZone) !== key);
  const at = (ms: number) => {
    const clock = formatZonedClock(ms, timeZone);
    return overnight ? `${dayShort(dateKeyOf(ms, timeZone))} ${clock}` : clock;
  };
  const range = `${at(b.startMs)}${overnight ? ' – ' : '–'}${b.ongoing ? 'now' : at(b.endMs)}`;
  if (b.kind === 'fire') return `${b.label} ${range}`;
  const reason = b.maintenance?.reason ?? b.liftRule?.reason;
  const holds = held.filter((it) => isHeld(it, [b])).length;
  return `${b.shortLabel} ${range}${reason ? ` · ${reason}` : ''}${holds ? ` · ${plural(holds, 'run', 'runs')} held` : ''}`;
}

// ---- the plan check, in words ------------------------------------------------------

export function targetText(days: number): string {
  return days === 1 ? 'target every day' : `target every ${days} days`;
}

/** "Targets: front of house every 2 days, back of house every 3 days" --
 *  the site's, set in Site settings, as Metrics reads them. */
export function targetsText(t: PlanCheck['targets']): string {
  const every = (n: number) => (n === 1 ? 'every day' : `every ${n} days`);
  return `Targets: front of house ${every(t.foh)}, back of house ${every(t.boh)}`;
}

/** The checked days' short counts by date. */
export function shortByDay(plan: PlanCheck | undefined): Map<DateKey, number> {
  const out = new Map<DateKey, number>();
  for (const d of plan?.days ?? []) if (d.checked) out.set(d.date, d.short_zones);
  return out;
}

/** The gaps a day falls in. */
export function gapsOn(plan: PlanCheck | undefined, key: DateKey): PlanCheckGap[] {
  return (plan?.gaps ?? []).filter((g) => g.from_date <= key && key <= g.to_date);
}

/** Whether the plan check covers `key` (today or later, inside the
 *  window it was asked for). */
export function isChecked(plan: PlanCheck | undefined, key: DateKey): boolean {
  return (plan?.days ?? []).some((d) => d.date === key && d.checked);
}

/**
 * One short zone, as the day panel lists it: "L16-10: no clean planned
 * 12-15 Nov · last planned 10 Nov · target every 2 days".
 *
 * What came before the gap is the LAST PLANNED run when there is one --
 * a clean still to come that the gap follows, which "last cleaned" would
 * claim had already happened -- else the last clean on record, else
 * "never cleaned".
 */
export function gapLine(g: PlanCheckGap, timeZone: string, todayKey: DateKey): string {
  return `${g.zone}: no clean planned ${spanText(g.from_date, g.to_date)} · ${lastText(g, timeZone, todayKey)} · ${targetText(g.target_days)}`;
}

function lastText(
  z: Pick<PlanCheckGap, 'last_cleaned_ms'> & { last_planned_ms?: number | null },
  timeZone: string,
  todayKey: DateKey,
): string {
  if (z.last_planned_ms !== null && z.last_planned_ms !== undefined) {
    return `last planned ${msDayMonth(z.last_planned_ms, timeZone, todayKey)}`;
  }
  if (z.last_cleaned_ms !== null) {
    return `last cleaned ${msDayMonth(z.last_cleaned_ms, timeZone, todayKey)}`;
  }
  return 'never cleaned';
}

/** A zone with no clean planned in the month, after its name: "last
 *  cleaned 28 Oct · short from 2 Nov · target every 2 days". It says when
 *  the zone goes short, past the month when that is when: the api-server
 *  looks out to `horizon_to_date` (decision 12, Akash 2026-10-31). On 31
 *  Oct a zone cleaned the day before read "on target today" and gave no
 *  date, though with nothing planned it goes short on 1 Nov; it now reads
 *  "short from 1 Nov". With nothing planned it stays short once it is, so
 *  a zone short on the month's first day is "short all month": "short from
 *  1 Dec" read as fine until then, for a zone short for weeks already
 *  (review, 2026-10-31). One short today says "short now", whether it goes
 *  short today or has been for weeks: "short from today" read as the
 *  first short day for a zone short since 30 Oct (Akash, 2026-10-31). One
 *  on target through those days says "on target to at least 27 Nov": "on
 *  target to 27 Nov" read as an end date, and with a 60-day target the date
 *  moved with the month viewed (Akash chose "past"; "at least" because the
 *  api-server only looks that far, and a 29-day zone can go short on the
 *  28th). `horizon_to_date` is null only when nothing is checked, and then
 *  no zone is listed. One held into a month ahead by a clean booked before
 *  it names that clean (`lastPlannedBefore`). */
export function unscheduledLine(
  z: PlanCheckUnscheduledZone,
  plan: Pick<PlanCheck, 'today' | 'checked_from' | 'to_date' | 'horizon_to_date'>,
  timeZone: string,
): string {
  const from =
    z.short_from_date === null
      ? `on target to at least ${dayMonth(plan.horizon_to_date ?? plan.to_date)}`
      : z.short_from_date === plan.today
        ? 'short now'
        : z.short_from_date === plan.checked_from
          ? 'short all month'
          : `short from ${dayMonth(z.short_from_date)}`;
  const planned = lastPlannedBefore(z, plan, timeZone);
  const last = planned
    ? `last planned ${dayMonth(planned, parts(plan.today).year)}`
    : lastText(z, timeZone, plan.today);
  return `${last} · ${from} · ${targetText(z.target_days)}`;
}

/**
 * The day of the clean still to come that keeps a zone with no clean
 * planned in the month on target into it, when that and not its last
 * clean is what holds it; else null. In a month ahead a clean booked late
 * in the month before can: "L6-West: last cleaned 30 Oct · short from 2
 * Dec · target every 2 days" read as false, held as it was by a clean on
 * 30 Nov the line did not name (review, 2026-10-31). It says "last
 * planned 30 Nov", as `gapLine` does before a gap.
 *
 * Exact from the wire, with no new field: short on D means nothing falls
 * on D-(N-1)..D, so a zone on target the day before its `short_from_date`
 * was held by something on exactly `short_from_date` - N. A run counts
 * from today on, and one from the month's first checked day would have
 * made the zone scheduled, so a run can hold it only from today to the day
 * before that; and only when its last clean did not fall on that day.
 */
function lastPlannedBefore(
  z: PlanCheckUnscheduledZone,
  plan: Pick<PlanCheck, 'today' | 'checked_from'>,
  timeZone: string,
): DateKey | null {
  // Short on the first checked day, nothing says it was on target the day
  // before, and so nothing names what held it.
  if (z.short_from_date === null || plan.checked_from === null) return null;
  if (z.short_from_date <= plan.checked_from) return null;
  const held = addDays(z.short_from_date, -z.target_days);
  if (held < plan.today || held >= plan.checked_from) return null;
  if (z.last_cleaned_ms !== null && dateKeyOf(z.last_cleaned_ms, timeZone) >= held) return null;
  return held;
}

/** A zone the plan leaves short somewhere in the window, all its gaps
 *  together: the month summary lists one row per zone, not per gap, so a
 *  corridor cleaned every third day against a two-day target is one line
 *  and not ten. */
export interface ShortZone {
  zone: string;
  /** Short days in all. */
  days: number;
  /** Its first short day. */
  first: DateKey;
  /** Its last short day. */
  last: DateKey;
  /** Its runs of short days, in order. */
  spells: number;
  /** The first two of them, which is all the row ever names. */
  firstSpells: Array<{ from: DateKey; to: DateKey }>;
}

export function shortZones(plan: PlanCheck): ShortZone[] {
  const byZone = new Map<string, ShortZone>();
  const gaps = [...plan.gaps].sort((a, b) => a.from_date.localeCompare(b.from_date));
  for (const g of gaps) {
    const n = daysInclusive(g.from_date, g.to_date);
    const spell = { from: g.from_date, to: g.to_date };
    const row = byZone.get(g.zone);
    if (!row) {
      byZone.set(g.zone, {
        zone: g.zone,
        days: n,
        first: g.from_date,
        last: g.to_date,
        spells: 1,
        firstSpells: [spell],
      });
    } else {
      row.days += n;
      row.last = g.to_date;
      row.spells += 1;
      if (row.firstSpells.length < 2) row.firstSpells.push(spell);
    }
  }
  return [...byZone.values()].sort(
    (a, b) => a.first.localeCompare(b.first) || a.zone.localeCompare(b.zone),
  );
}

/** "short on 1 Nov", "short 12-15 Nov", "short 1-6 Nov, 9-30 Nov", and
 *  past two runs of days "short 9 days between 6 and 30 Nov". A count
 *  anchored to the first day ("short 9 days from 6 Nov") read as one
 *  unbroken run for a corridor short every third day (review, 2026-10-31). */
export function shortZoneText(z: ShortZone): string {
  if (z.spells > 2) return `short ${z.days} days between ${betweenText(z.first, z.last)}`;
  const spells = z.firstSpells.map((s) => spanText(s.from, s.to)).join(', ');
  return z.days === z.spells ? `short on ${spells}` : `short ${spells}`;
}

/** "6 and 30 Nov", "28 Nov and 3 Dec". */
function betweenText(from: DateKey, to: DateKey): string {
  const a = parts(from);
  const b = parts(to);
  return a.year === b.year && a.month === b.month
    ? `${a.day} and ${dayMonth(to)}`
    : `${dayMonth(from)} and ${dayMonth(to)}`;
}

function daysInclusive(from: DateKey, to: DateKey): number {
  let n = 1;
  for (let d = from; d < to; d = addDays(d, 1)) n += 1;
  return n;
}

export interface PlanSummary {
  /** "Rest of November: 4 zones go short on 9 days". */
  headline: string;
  shortZones: number;
  shortDays: number;
  /** "3 zones have no clean planned in the next 4 weeks"; null when every
   *  zone has one. */
  unscheduledText: string | null;
}

/** The days a zone with "no clean planned" has none in, as the answer
 *  says (`horizon_to_date`): "in the next 4 weeks" when they run past the
 *  month, which is the month today is in with fewer than 28 days left;
 *  else "for the rest of November" when the month's first days are past,
 *  and "in November" when all of it is checked.
 *
 *  A zone is listed as having none when it has no run in THOSE days, not
 *  when it has no schedule. It was called a zone with "no schedule" for
 *  a weekly clean with no run left in October (review, 2026-10-31); then
 *  on 31 October such a zone was "no clean planned for the rest of
 *  October" and left out of the day's "zones short" although it was short
 *  that day. Now the api-server looks 4 weeks ahead (decision 9), counts
 *  it short, and lists only a zone with nothing in the 4 weeks. */
export function planDaysText(
  plan: Pick<PlanCheck, 'from_date' | 'to_date' | 'checked_from' | 'horizon_to_date'>,
  monthKey: DateKey,
): string {
  if (plan.horizon_to_date !== null && plan.horizon_to_date > plan.to_date) {
    return 'in the next 4 weeks';
  }
  const month = MONTH_LONG[parts(monthKey).month - 1]!;
  return plan.checked_from !== null && plan.checked_from > plan.from_date
    ? `for the rest of ${month}`
    : `in ${month}`;
}

/** The month at a glance, for the plan-check roles; null when nothing in
 *  the month is checked (all of it is past). */
export function planSummary(plan: PlanCheck, monthKey: DateKey): PlanSummary | null {
  if (plan.checked_from === null) return null;
  const month = MONTH_LONG[parts(monthKey).month - 1]!;
  const prefix = sameMonth(plan.today, monthKey) ? `Rest of ${month}` : month;
  const shortZones = new Set(plan.gaps.map((g) => g.zone)).size;
  const shortDays = plan.days.filter((d) => d.checked && d.short_zones > 0).length;
  const unscheduled = plan.unscheduled_zones.length;
  const headline =
    shortZones > 0
      ? `${prefix}: ${plural(shortZones, 'zone goes', 'zones go')} short on ${plural(shortDays, 'day', 'days')}`
      : unscheduled > 0
        ? `${prefix}: every zone with a clean planned stays on target`
        : `${prefix}: every zone stays on target`;
  return {
    headline,
    shortZones,
    shortDays,
    unscheduledText:
      unscheduled === 0
        ? null
        : `${plural(unscheduled, 'zone has', 'zones have')} no clean planned ${planDaysText(plan, monthKey)}`,
  };
}

// ---- New task on this day ------------------------------------------------------------

/**
 * Where "New task on this day" opens the form: 09:00 on a day still to
 * come, and on today the next whole hour (no later than 23:55). As a
 * browser `Date` whose own clock reads the SITE's wall time, because that
 * is how the form reads its start field (`TaskDispatchForm`'s
 * `prefillStart`): it takes the digits and reads them on the site's clock.
 */
export function newTaskStart(
  key: DateKey,
  todayKey: DateKey,
  nowMs: number,
  timeZone: string,
): Date {
  const { year, month, day } = parts(key);
  if (key !== todayKey) return new Date(year, month - 1, day, 9, 0);
  const hour = zonedParts(nowMs, timeZone).hour;
  const next = Math.min((hour + 1) * 60, 23 * 60 + 55);
  return new Date(year, month - 1, day, Math.floor(next / 60), next % 60);
}
